/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SESSION_RESET } from './SessionActionTypes'
import { SessionData } from 'types/responses/SessionResponse'
import { Profile } from 'types/entities/Profile'
import { Driver } from 'types/entities/Driver'
import { Language } from 'types/entities/Language'
import { Ticket } from 'types/entities/Ticket'
import { MyStats } from 'types/entities/MyStats'
import { Notification } from 'types/entities/Notification'
import { Ranking } from 'types/entities/Ranking'

type SessionResultScalar = string | number | boolean | null
type SessionResultObject = Record<string, SessionResultScalar | SessionResultScalar[]>
type SessionResultValue = SessionResultScalar | SessionResultScalar[] | SessionResultObject

export type ReducerSessionPushNotificationAction = PayloadAction<Notification>

export interface SessionState {
    notifications: Notification[]
    session: SessionData | null
    profile: Profile | null
    ranking: Ranking[] | null
    result: SessionResultValue | null
    results: SessionResultValue[]
    language: Language | null
    themeMode: 'light' | 'dark' | null
    tickets: Ticket[] | []
    myStats: MyStats
}

const initialState: SessionState = {
    notifications: [],
    session: null,
    profile: null,
    ranking: null,
    result: null,
    results: [],
    language: null,
    themeMode: null,
    tickets: [],
    myStats: {
        bestLap: [],
        me: null,
        stats: [],
    },
}

const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        setNotifications(state, action: PayloadAction<Notification[]>) {
            state.notifications = action.payload
        },
        pushNotification(state, action: PayloadAction<Notification>) {
            const index = state.notifications.findIndex((notification) => notification.id === action.payload.id)
            if (index !== -1) {
                state.notifications[index] = action.payload
            } else {
                state.notifications.push(action.payload)
            }
        },
        setSession(state, action: PayloadAction<SessionData | null>) {
            state.session = action.payload
        },
        setProfile(state, action: PayloadAction<Profile | null>) {
            state.profile = action.payload
        },
        setCheckin(state, action: PayloadAction<Driver>) {
            if (state.profile) {
                state.profile.checkin_time = action.payload.checkin_time
                state.profile.checked = action.payload.checked
            }
        },
        setResult(state, action: PayloadAction<SessionResultValue>) {
            state.result = action.payload
        },
        setRanking(state, action: PayloadAction<Ranking[] | null>) {
            state.ranking = action.payload
        },
        setResults(state, action: PayloadAction<SessionResultValue[]>) {
            state.results = action.payload
        },
        setMyStats(state, action: PayloadAction<MyStats>) {
            state.myStats = action.payload
        },
        setTickets(state, action: PayloadAction<Ticket[]>) {
            state.tickets = action.payload
        },
        setLanguage(state, action: PayloadAction<Language | null>) {
            state.language = action.payload
        },
        setThemeMode(state, action: PayloadAction<'light' | 'dark' | null>) {
            state.themeMode = action.payload
        },
        reset() {
            return initialState
        },
        resetState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(SESSION_RESET, () => {
            return initialState
        })
    },
})

export const {
    setNotifications,
    pushNotification,
    setSession,
    setProfile,
    setCheckin,
    setResult,
    setRanking,
    setResults,
    setMyStats,
    setTickets,
    setLanguage,
    setThemeMode,
    reset,
    resetState,
} = sessionSlice.actions

export default sessionSlice.reducer
